import { useCallback, useMemo, useRef } from "react";
import type { GridReadyEvent, RowClickedEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import type { ProductSize } from "../models/ProductSize";
import { useArrowRowNavigation } from "@/controls/ag-grid/useArrowRowNavigation";
import {
	OneLineCellStyles,
	LIST_ROW_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import { useTableData } from "../hooks/useTableData";

interface ProductsTableProps {
	products: ProductSize[];
	selectedProduct: ProductSize | null;
	onRowSelected: (product: ProductSize | null) => void;
	onDelete: (product: ProductSize) => void;
}

export const ProductsTable = ({
	products,
	selectedProduct,
	onRowSelected,
	onDelete,
}: ProductsTableProps) => {
	const gridRef = useRef<AgGridReact<ProductSize>>(null);
	const { columnDefs } = useTableData({ onDelete });

	// One line per product: the global default wraps numbers between characters in narrow columns.
	const defaultColDef = useMemo(
		() => ({ ...globalDefaultColDef, wrapText: false, autoHeight: false }),
		[],
	);

	const onGridReady = useCallback(
		(_params: GridReadyEvent<ProductSize>) => {},
		[],
	);

	const onRowClicked = useCallback(
		(event: RowClickedEvent<ProductSize>) => {
			if (event.data) {
				onRowSelected(event.data);
			}
		},
		[onRowSelected],
	);

	const onCellKeyDown = useArrowRowNavigation<ProductSize>(onRowSelected);

	const getRowId = useCallback((params: { data: ProductSize }) => {
		return params.data.Id?.toString() ?? "";
	}, []);

	return (
		<>
			<OneLineCellStyles />
			<AgGridReact<ProductSize>
				ref={gridRef}
				rowData={products}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				onGridReady={onGridReady}
				onRowClicked={onRowClicked}
				onCellKeyDown={onCellKeyDown}
				getRowId={getRowId}
				rowSelection="single"
				suppressRowClickSelection={false}
				suppressContextMenu
				alwaysShowVerticalScroll
				suppressColumnVirtualisation
				animateRows={false}
				rowClass={LIST_ROW_CLASS}
				rowHeight={28}
				headerHeight={32}
				enableBrowserTooltips
				getRowStyle={(params) => {
					if (selectedProduct?.Id === params.data?.Id) {
						return { backgroundColor: "#e3f2fd", color: "inherit" };
					}
					if (params.data && !params.data.Active) {
						return { backgroundColor: "#f5f5f5", color: "#9e9e9e" };
					}
					return undefined;
				}}
			/>
		</>
	);
};
